import { useState } from 'react';
import { Button, Table, Spin, Modal, Tooltip } from "antd";
import Header from "../../../components/HeaderList";
import { ReactComponent as Edit } from "../../../assets/icons/edit.svg";
import { ReactComponent as Trash } from "../../../assets/icons/trash.svg";

import { getColumnSearchProps } from "../../../utils/tables";
import Editor from "./Editor";
import styles from "../styles/AreasUI.module.css";

const AreasUI = (props) => {
  const {
    loading,
    areas,
    area,
    openEditor,
    editorVisible,
    closeEditor,
    handleArea,
    confirmDelete
  } = props;
  const [filteredInfo, setFilteredInfo] = useState({});

  const handleTableChange = (pagination, filters) => {
    setFilteredInfo(filters);
  };

  const formatDispatchOrder = (dispatchOrder) => {
    if (dispatchOrder === "PEYA_FIRST") {
      return "PedidosYa -> Drivers";
    }

    return "Drivers -> PedidosYa";
  };

  const parsePriorityList = (rawValue) => {
    if (Array.isArray(rawValue)) {
      return rawValue;
    }

    if (typeof rawValue !== 'string' || !rawValue.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const parseDispatchServices = (rawValue) => {
    if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
      return rawValue;
    }

    if (typeof rawValue !== 'string' || !rawValue.trim()) {
      return {};
    }

    try {
      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      return {};
    }
  };

  const getDelayFallbackMinutes = (record) => {
    const priority = parsePriorityList(
      record.dispatch_services_priority || record.dispatchServicesPriority
    );
    const dispatchServices = parseDispatchServices(
      record.dispatch_services || record.dispatchServices
    );
    const firstServiceKey = priority[0];

    if (firstServiceKey && dispatchServices[firstServiceKey]) {
      const timeRaw = dispatchServices[firstServiceKey].time_minutes
        ?? dispatchServices[firstServiceKey].timeMinutes;
      const parsedTime = Number.parseInt(timeRaw, 10);

      if (Number.isInteger(parsedTime) && parsedTime >= 0) {
        return parsedTime;
      }
    }

    return Number.isInteger(record.peya_delay_minutes) ? record.peya_delay_minutes : 3;
  };

  const renderBranches = (areaId) => {
    const selAre = areas.find(x => x.id === areaId);
    const areaBranches = selAre.sucursales;
    if (areaBranches.length === 0) {
      return <span className={styles.noBranches}>Sin sucursales</span>;
    }
    
    if (areaBranches.length <= 3) {
      return (
        <div className={styles.branchesList}>
          {areaBranches.map((branch, index) => (
            <div key={branch.id} className={styles.branchItem}>
              {branch.zone}
            </div>
          ))}
        </div>
      );
    }
    
    const tooltipContent = (
      <div>
        {areaBranches.map((branch) => (
          <div key={branch.id} style={{ marginBottom: '4px' }}>
            {branch.zone}
          </div>
        ))}
      </div>
    );
    
    return (
      <Tooltip title={tooltipContent} placement="topLeft">
        <div className={styles.branchesList}>
          {areaBranches.slice(0, 2).map((branch, index) => (
            <div key={branch.id} className={styles.branchItem}>
              {branch.zone}
            </div>
          ))}
          <div className={styles.moreBranches}>
            +{areaBranches.length - 2} más
          </div>
        </div>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      align: "center",
      width: 80,
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      align: "center",
      ...getColumnSearchProps("nombre"),
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      align: "center",
      ...getColumnSearchProps("descripcion"),
      render: (text) => text || "-",
    },
    {
      title: "Orden de despacho",
      dataIndex: "dispatch_order",
      align: "center",
      width: 240,
      filters: [
        { text: "Drivers -> PedidosYa", value: "DRIVER_FIRST" },
        { text: "PedidosYa -> Drivers", value: "PEYA_FIRST" },
      ],
      onFilter: (value, record) => record.dispatch_order === value,
      filteredValue: filteredInfo.dispatch_order || null,
      render: (dispatchOrder) => formatDispatchOrder(dispatchOrder),
    },
    {
      title: "Delay fallback",
      dataIndex: "peya_delay_minutes",
      align: "center",
      width: 160,
      render: (_, record) => `${getDelayFallbackMinutes(record)} min`,
    },
    {
      title: "Sucursales",
      dataIndex: "id",
      align: "center",
      width: 200,
      render: (areaId) => renderBranches(areaId),
    },
    /*
    {
      title: "Estado",
      dataIndex: "active",
      align: "center",
      width: 120,
      filters: [
        { text: "Activo", value: 1 },
        { text: "Inactivo", value: 0 },
      ],
      onFilter: (value, record) => record.active === value,
      filteredValue: filteredInfo.active || null,
      render: (val, record) => (
        <Switch
          checked={val === 1}
          onChange={() => handleToggleStatus(record)}
        />
      ),
    },
    */
    {
      title: "Acciones",
      align: "center",
      dataIndex: "id",
      width: 120,
      render: (id, record) => (
        <div className={styles.actions}>
          <div className={styles.edit} onClick={() => openEditor(record)} >
            <Edit />
          </div>
          <div className={styles.divider} />
          <div className={styles.delete}  onClick={() => confirmDelete(id)} >
            <Trash />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.view}>
      <Header
        back="/ajustes"
        title="Listado de Áreas"
        actions={
          <div className={styles.buttons}>
            <Button type="primary"  size="middle" onClick={() => openEditor(null)} >
              AGREGAR ÁREA
            </Button>
          </div>
        }
      />
      {loading ? (
        <div className={styles.loader}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <Table 
            rowKey="id" 
            columns={columns} 
            dataSource={areas} 
            onChange={handleTableChange} 
            pagination={{ pageSize: 15 }}
            scroll={{ x: 800 }}
          />
        </div>
      )}
      <Modal
        destroyOnClose
        closable={false}
        visible={editorVisible}
        footer={false}
        onCancel={closeEditor}
        width={600}
      >
        <Editor
          close={closeEditor}
          area={area}
          handleArea={handleArea}
        />
      </Modal>
    </div>
  );
};

export default AreasUI;
